import { useEffect, useState } from 'react';
import { Btn } from './Btn';
import {
  disablePush,
  enablePush,
  getCurrentSubscription,
  isPushSupported,
  type EnableOutcome,
} from '../lib/push';

type State = 'loading' | 'unsupported' | 'denied' | 'off' | 'on';

export function PushSettings() {
  const [state, setState] = useState<State>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) { if (!cancelled) setState('unsupported'); return; }
      if (Notification.permission === 'denied') { if (!cancelled) setState('denied'); return; }
      const sub = await getCurrentSubscription();
      if (!cancelled) setState(sub ? 'on' : 'off');
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEnable = async () => {
    setBusy(true); setError(null);
    try {
      const outcome: EnableOutcome = await enablePush();
      if (outcome === 'enabled') setState('on');
      else if (outcome === 'denied') setState('denied');
      else if (outcome === 'unsupported') setState('unsupported');
      else if (outcome === 'server-disabled') setError('Powiadomienia są tymczasowo wyłączone na serwerze.');
    } catch (e) {
      setError('Nie udało się włączyć powiadomień. Spróbuj ponownie.');
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true); setError(null);
    try {
      await disablePush();
      setState('off');
    } catch (e) {
      setError('Nie udało się wyłączyć powiadomień.');
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h3 className="serif" style={{ margin: 0, fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
        Powiadomienia push
      </h3>
      <div style={{ fontSize: 13, color: '#5b6578', marginBottom: 16 }}>
        Otrzymuj powiadomienia o zaproszeniach do pokojów i innych ważnych zdarzeniach
        nawet gdy aplikacja jest zamknięta.
      </div>

      {state === 'loading' && <div style={{ fontSize: 13, color: '#5b6578' }}>Sprawdzam status…</div>}

      {state === 'unsupported' && (
        <Hint tone="muted">
          Twoja przeglądarka nie obsługuje powiadomień push. Na iOS dodaj aplikację do ekranu
          początkowego i otwórz ją stamtąd (wymagany iOS 16.4 lub nowszy).
        </Hint>
      )}

      {state === 'denied' && (
        <Hint tone="warn">
          Zablokowałeś/aś powiadomienia w przeglądarce. Aby je włączyć, zmień to w
          ustawieniach strony (kłódka obok adresu) i odśwież.
        </Hint>
      )}

      {state === 'off' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Btn variant="primary" size="md" onClick={handleEnable} disabled={busy}>
            {busy ? 'Włączanie…' : 'Włącz powiadomienia'}
          </Btn>
        </div>
      )}

      {state === 'on' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#0f6b34', fontWeight: 600 }}>● Włączone</span>
          <Btn variant="ghost" size="md" onClick={handleDisable} disabled={busy}>
            {busy ? 'Wyłączanie…' : 'Wyłącz'}
          </Btn>
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: 'rgba(208,70,60,.08)',
            color: '#8a2a22',
            borderRadius: 10,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function Hint({ tone, children }: { tone: 'muted' | 'warn'; children: React.ReactNode }) {
  const colors = tone === 'warn'
    ? { bg: 'rgba(244,185,58,.12)', fg: '#6b4a00' }
    : { bg: 'rgba(15,30,61,.05)', fg: '#5b6578' };
  return (
    <div style={{ padding: 12, background: colors.bg, color: colors.fg, borderRadius: 10, fontSize: 13 }}>
      {children}
    </div>
  );
}
